import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import CloseIcon from '@mui/icons-material/Close';
import { ReviewField } from 'api';
import { CorrectionInput } from './CorrectionInput';
import { UnavailableIndicator } from './UnavailableIndicator';
import {
  FieldDecisionState,
  hasEnteredCorrection,
  removeReference,
  setShouldBeBlank,
  toggleDecision,
} from '../decisionState';

type FieldCardProps = {
  field: ReviewField;
  /** Display label, e.g. `cis` inside the `sites[0]` panel. The field name sent
   * to the API is unchanged. */
  labelOverride?: string;
  decision: FieldDecisionState;
  active: boolean;
  onSelect: () => void;
  onDecisionChange: (patch: Partial<FieldDecisionState>) => void;
  selectingReference: boolean;
  onToggleSelectReference: () => void;
};

export const FieldCard = ({
  field,
  labelOverride,
  decision,
  active,
  onSelect,
  onDecisionChange,
  selectingReference,
  onToggleSelectReference,
}: FieldCardProps) => {
  const handleToggle = (
    _: React.MouseEvent<HTMLElement>,
    next: 'correct' | 'incorrect' | null
  ) => {
    if (next === null) return;
    // Confirm before discarding an entered correction. References survive the toggle.
    if (
      next === 'correct' &&
      hasEnteredCorrection(decision) &&
      !window.confirm('Discard the entered correction?')
    ) {
      return;
    }
    onDecisionChange(toggleDecision(decision, next));
  };

  const showSchemaNotice =
    field.schema == null ||
    (field.schema.fieldType !== 'enum' && field.schema.fieldType !== 'string');

  return (
    <Card
      variant="outlined"
      onClick={onSelect}
      sx={{
        mb: 2,
        cursor: 'pointer',
        borderColor: active ? 'primary.main' : undefined,
        borderWidth: active ? 2 : 1,
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2">
            {labelOverride ?? field.fieldName}
          </Typography>
          <Chip
            label={
              field.confidenceScore == null
                ? 'Unavailable'
                : `${Math.round(field.confidenceScore * 100)}%`
            }
            size="small"
            variant="outlined"
          />
        </Box>

        <Typography variant="body2" sx={{ mb: 1 }}>
          {field.value ?? <UnavailableIndicator label="No value extracted" />}
        </Typography>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={
            decision.isCorrect === null
              ? null
              : decision.isCorrect
                ? 'correct'
                : 'incorrect'
          }
          onChange={handleToggle}
          onClick={e => e.stopPropagation()}
        >
          <ToggleButton value="correct">Correct</ToggleButton>
          <ToggleButton value="incorrect">Incorrect</ToggleButton>
        </ToggleButtonGroup>

        {decision.isCorrect === false && (
          <Box onClick={e => e.stopPropagation()} sx={{ mt: 1.5 }}>
            <CorrectionInput
              schema={field.schema}
              value={decision.correctedValue}
              onChange={v => onDecisionChange({ correctedValue: v })}
              disabled={decision.shouldBeBlank}
            />
            {/* The only way to record "the document has no value here"; an empty box isn't one. */}
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={decision.shouldBeBlank}
                  onChange={e => onDecisionChange(setShouldBeBlank(e.target.checked))}
                />
              }
              label="Should be blank"
              slotProps={{ typography: { variant: 'body2' } }}
            />
            {showSchemaNotice && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                Not found in the extraction schema — entered as free text.
              </Typography>
            )}
          </Box>
        )}

        {/* On a Correct field, a reference means the model's citation was wrong.
            A blank correction carries none. */}
        {decision.isCorrect != null && !decision.shouldBeBlank && (
          <Box
            onClick={e => e.stopPropagation()}
            sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
          >
            {decision.citationOverrides.map((reference, i) => (
              <Chip
                key={`${reference.start}-${reference.stop}`}
                size="small"
                variant="outlined"
                color="info"
                icon={<HighlightAltIcon />}
                deleteIcon={<CloseIcon />}
                onDelete={() =>
                  onDecisionChange({
                    citationOverrides: removeReference(decision.citationOverrides, i),
                  })
                }
                label={`Reference: "${reference.selectedText}"`}
                sx={{ maxWidth: '100%' }}
              />
            ))}
            <Button
              size="small"
              variant={selectingReference ? 'contained' : 'outlined'}
              startIcon={<HighlightAltIcon />}
              onClick={onToggleSelectReference}
            >
              {selectingReference
                ? 'Highlight text in the document…'
                : decision.citationOverrides.length > 0
                  ? 'Add another reference'
                  : 'Select text reference'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
